/**
 * frame-source.js — strictly-ordered frames of a clip, for the tools that work
 * frame by frame: /video-bg-remover (the matte pass, the blur export, «keep an
 * object»), /remove-object-from-video, /remove-text-from-video and
 * /text-behind-image.
 *
 *   window.VbrFrameSource.iterate(opts, onFrame) → Promise<{ frames, method }>
 *     opts:    { file, url, fps, trimStart, trimEnd, width, height,
 *                abortSignal, noSeekFallback?, times? }
 *     onFrame: async (canvas, frameIndex, tSec) => void
 *   window.VbrFrameSource.frameTimes(file, {duration}) → Promise<{times, avgFps, vfr} | null>
 *   window.VbrFrameSource.timeline(times) → {n, times, at(k), onScreen(t), firstFrom(t)}
 *
 * THE CONTRACT
 *   · N = round((trimEnd − trimStart) · fps) frames; index i is the picture a
 *     <video> shows at trimStart + i/fps;
 *   · with `times` (seconds, ascending — the clip's own frames, frameTimes()):
 *     N = times.length and index i is the picture shown at times[i]. A clip
 *     whose frame rate changes inside the file (a phone in dim light, a screen
 *     recording, an edited clip) has no grid of «fps» that holds its frames:
 *     at the average rate the fast stretches lose frames and the slow ones
 *     repeat (#9567: 82 of 581 frames never reached the export);
 *   · every index exactly once, in order, and NEVER two onFrame calls at once:
 *     RVM is recurrent, and an ORT session runs one run() at a time;
 *   · onFrame is awaited before the next frame is asked for (back-pressure);
 *     the canvas is valid until onFrame settles — copy what you keep (every
 *     caller does: getImageData, or drawImage into a canvas of its own);
 *   · an error thrown BY onFrame is the caller's: it ends the iteration as it
 *     is and is never retried through the other lane.
 *
 * LANE 1 — the decoder, pulled one frame at a time
 *   VideoMergeWebCodecs.DecodeStream + ClipCursor (video-merge-webcodecs.js,
 *   the house primitive for «decode a clip into frames in order»): the sample
 *   table is read lazily and the bytes in ≤ 8 MB windows, mp4/mov and WebM/MKV
 *   alike; at most 8 chunks wait inside the decoder; a decoder that dies or
 *   goes silent is replaced from the last keyframe (twice) and the frames it
 *   already gave are not given again; frames come out display-oriented.
 *
 * LANE 2 — <video> seeking, resumed at the first index lane 1 did not deliver
 *   (a browser without WebCodecs, a codec its decoder refuses, a container
 *   neither demuxer reads). Never a restart: every caller has already acted
 *   on the frames it got.
 *
 * WHY LANE 1 CHANGED (2026-10-02). It was WebCodecsFrameExtractor, a PUSH
 * design: its feed loop called onFrame from inside the decode loop and policed
 * the decoder with a 10-s «no output» watchdog. A hardware decoder renders
 * into a fixed pool of pictures, and the frames queued for a slow consumer
 * hold that pool, so while RVM spent seconds on one frame — a phone, the first
 * frame's GPU warm-up, a step-down to the CPU, a hidden tab — the decoder sat
 * on a chunk it could not take and the watchdog called it a wedge. RU+intl,
 * 18.09–02.10, /video-bg-remover alone: 448 sessions, `queue 1`, fed − decoded
 * 3–10, «silence» 16–100 s. Every one fell to <video> seeking, which on an
 * iPhone or in a hidden tab ended in `decode:load-timeout` — the commonest
 * error of the matte pass, «Не удаётся прочитать это видео» after «Убрать
 * фон». And because the watchdog rejected a Promise.race while onFrame was
 * still running, the floor's first onFrame overlapped the abandoned one on the
 * same session: «Session already started» (31 sessions), which the engine
 * read as a GPU failure and answered by stepping down to the CPU. Android
 * reached review in 55 % of started runs, iOS in 60 %, Windows in 89 %.
 *   A pull design has nothing to fool: the decoder is asked for a frame only
 * when the consumer is ready for one, and its silence is measured only while
 * somebody waits for it (DecodeStream, DECODE_SILENT_MS).
 */
(function (root) {
    'use strict';

    function makeCanvas(w, h) {
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        return c;
    }

    function aborted(sig) { return !!(sig && sig.aborted); }
    function abortError() { return new DOMException('aborted', 'AbortError'); }
    function feature(name, data) {
        try { root.__aeFeature && root.__aeFeature(name, data); } catch (_) { }
    }
    /* Deadlines that wait for the person, not the clock: a hidden tab does not
       load or decode media, and that is not a failure (timbrica-core.js). */
    function after(fn, ms) {
        if (typeof root.__visibleTimeout === 'function') return root.__visibleTimeout(fn, ms);
        var h = setTimeout(fn, ms);
        return function () { clearTimeout(h); };
    }

    /* A <video> the page cannot see is a video iOS never decodes: WebKit parks
       a detached media element — readyState stays at 1, `loadeddata` never
       fires, no error either. The seek floor below created exactly such an
       element, so on an iPhone it waited forever on its very first frame:
       /remove-object-from-video logged sessions still «preparing the video»
       twelve minutes after a WebCodecs decoder failure (prod, 14.09.2026).
       The page's own display video already lives in a 1×1 visible host for
       this reason; this is the same host, shared by every fallback element. */
    var HOST_ID = 'vbr-media-host';
    function attachHidden(video) {
        if (typeof document === 'undefined' || !document.body || !video) return;
        var host = document.getElementById(HOST_ID);
        if (!host) {
            host = document.createElement('div');
            host.id = HOST_ID;
            host.setAttribute('aria-hidden', 'true');
            host.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;overflow:hidden;opacity:0.01;pointer-events:none;z-index:-1';
            document.body.appendChild(host);
        }
        video.setAttribute('playsinline', '');
        video.style.cssText = 'width:1px;height:1px';
        host.appendChild(video);
    }
    function detachHidden(video) {
        try { if (video && video.parentNode) video.parentNode.removeChild(video); } catch (_) { }
    }
    /* `loadeddata` with a deadline: an element that never gets there must end in
       an error the caller can act on, not in a promise that never settles. The
       deadline counts VISIBLE time: a tab in the background does not load
       media at all, and «took longer than 20 s while nobody was looking» is not
       an unreadable video. */
    function whenLoaded(video, timeoutMs) {
        return new Promise(function (res, rej) {
            var done = false, cancel = null;
            function fin(ok, e) {
                if (done) return;
                done = true;
                if (cancel) cancel();
                if (ok) res(); else rej(e);
            }
            cancel = after(function () { fin(false, new Error('decode:load-timeout')); }, timeoutMs || 20000);
            video.addEventListener('loadeddata', function () { fin(true); }, { once: true });
            video.addEventListener('error', function () { fin(false, new Error('decode')); }, { once: true });
            if (video.readyState >= 2) fin(true);
        });
    }

    function untilAborted(p, sig) {
        if (!sig) return p;
        if (sig.aborted) return Promise.reject(abortError());
        return new Promise(function (res, rej) {
            function onAbort() { rej(abortError()); }
            sig.addEventListener('abort', onAbort, { once: true });
            p.then(function (v) { sig.removeEventListener('abort', onAbort); res(v); },
                   function (e) { sig.removeEventListener('abort', onAbort); rej(e); });
        });
    }

    /* The caller's own failure travels marked, so the dispatcher below never
       takes it for a decoding problem and replays the clip through the floor —
       which is what happened to an RVM error before: the seek lane re-ran the
       model on the frames that had just failed. */
    async function consume(onFrame, canvas, i, t) {
        try {
            await onFrame(canvas, i, t);
        } catch (e) {
            var err = (e && typeof e === 'object') ? e : new Error(String(e));
            try { err.fromConsumer = true; } catch (_) { }
            throw err;
        }
    }

    /* Which pictures an iteration hands out: the grid trimStart + i/fps, or the
       clip's own frame times (opts.times). With times the decode window is the
       span they cover — trimStart/trimEnd, when given, only widen it. */
    function planOf(opts) {
        var tt = opts.times;
        if (tt && tt.length) {
            var n = tt.length;
            var t0 = Math.min(tt[0], opts.trimStart != null ? opts.trimStart : tt[0]);
            var t1 = Math.max(tt[n - 1] + 1e-3, opts.trimEnd != null ? opts.trimEnd : 0);
            return {
                n: n, times: tt, trimStart: t0, trimEnd: t1,
                at: function (i) { return tt[i]; },
                mid: function (i) {
                    var gap = i + 1 < n ? tt[i + 1] - tt[i] : (i > 0 ? tt[i] - tt[i - 1] : 1 / 30);
                    return tt[i] + 0.5 * gap;
                }
            };
        }
        var fps = opts.fps;
        return {
            n: Math.max(1, Math.round((opts.trimEnd - opts.trimStart) * fps)), times: null,
            trimStart: opts.trimStart, trimEnd: opts.trimEnd,
            at: function (i) { return opts.trimStart + i / fps; }
        };
    }

    /* ───────────────── lane 1 — DecodeStream ───────────────── */

    function laneError(reason) {
        var e = new Error('decoder-lane:' + reason);
        e.lane = true;
        return e;
    }

    var VM_URL = '/js/video-merge-webcodecs.js';
    var vmLoading = null;
    function decoderModule() {
        var VM = root.VideoMergeWebCodecs;
        if (VM && VM.DecodeStream && VM.demuxClip) return Promise.resolve(VM);
        if (vmLoading) return vmLoading;
        vmLoading = new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            // versioned: unversioned code URLs are served immutable for months
            s.src = (typeof root.__jsSrc === 'function') ? root.__jsSrc(VM_URL) : VM_URL;
            s.onload = function () {
                var M = root.VideoMergeWebCodecs;
                if (M && M.DecodeStream && M.demuxClip) { resolve(M); return; }
                vmLoading = null;
                reject(laneError('no-module'));
            };
            s.onerror = function () { vmLoading = null; reject(laneError('module-load-failed')); };
            document.head.appendChild(s);
        });
        return vmLoading;
    }

    /* Decoded frames the stream may hold for us. A held VideoFrame is a
       decoder surface (3 MB at 1080p, 12 MB at 4K); a consumer that spends
       seconds on each needs one in hand and one coming, not ten. */
    var HWM = 3;
    /* When the lazy table cannot be read (a fragmented MP4, a moov the box
       scanner cannot reach) the demuxer reads the whole file and keeps every
       sample: ~2.7× the file in memory. A phone tab does not have that for a
       big file, and the seek floor is cheaper than a killed tab. The device's
       memory is the layout's answer (__memBudget): an iOS tab is small
       whatever its RAM; no answer at all keeps the small ceiling. */
    function eagerCeiling() {
        var nav = root.navigator || {};
        var B = root.__memBudget;
        var phone = /Mobi|Android|iPhone|iPad/i.test(nav.userAgent || '');
        var small = !B || B.constrained() || B.gb() <= 4;
        return (phone || small) ? 160 * 1048576 : 640 * 1048576;
    }

    async function iterateDecoder(opts, onFrame, progress) {
        if (typeof VideoDecoder === 'undefined' || typeof VideoFrame === 'undefined') throw laneError('no-webcodecs');
        var VM = await decoderModule();
        // a container neither demuxer reads goes to the floor at once — the
        // eager mp4 reader would otherwise read the whole file to find that out
        if (!VM.isMp4Family(opts.file) && !VM.isMatroska(opts.file)) throw laneError('container');
        await VM.loadMp4Box();
        if (aborted(opts.abortSignal)) throw abortError();
        var d;
        try {
            d = await VM.demuxClip(opts.file, false, { matroska: true, slug: opts.slug, eagerMax: eagerCeiling() });
        } catch (e) {
            throw laneError('demux:' + String((e && e.message) || e).slice(0, 60));
        }
        if (!d || !d.samples || !d.samples.length) throw laneError('no-samples');
        // avc1/hvc1 carry their parameter sets in the description; without it
        // the decoder refuses the first chunk — say so now, not after a rescue.
        if (/^(avc1|hvc1)/i.test(d.codec || '') && !d.desc) throw laneError('no-description:' + d.codec);
        progress.codec = String(d.codec || '').slice(0, 24);
        progress.container = d.container || (d.lazy ? 'mp4' : 'mp4-eager');

        var fps = opts.fps;
        var plan = planOf(opts);
        var N = plan.n;
        var W = opts.width, H = opts.height;
        // кадр рисуется ниже через VideoOrientation.draw — копия при выдаче не нужна
        var stream = new VM.DecodeStream(d, Math.round(plan.trimStart * 1e6), Math.round(plan.trimEnd * 1e6), { hwm: HWM, deferRotation: true });
        var cursor = new VM.ClipCursor(stream, VM.sourceFpsOf(d));
        // Two canvases, alternating: the frame handed out stays intact while
        // the caller works on it, and a clip does not leave one canvas per
        // frame for the collector (WebKit counts canvas memory against a page
        // budget of its own).
        var ring = [makeCanvas(W, H), makeCanvas(W, H)];
        var ctxs = [ring[0].getContext('2d'), ring[1].getContext('2d')];
        var VO = root.VideoOrientation;
        try {
            for (var i = progress.emitted; i < N; i++) {
                if (aborted(opts.abortSignal)) throw abortError();
                var t = plan.at(i);
                var f = await untilAborted(cursor.frameAt(Math.round(t * 1e6)), opts.abortSignal);
                if (!f) throw laneError('no-frames:' + i);
                var k = i & 1, x = ctxs[k];
                x.clearRect(0, 0, W, H);
                // the frame may carry its rotation as a note (engines without
                // VideoFrame rotation) — draw() applies it either way
                if (VO && VO.draw) VO.draw(x, f, 0, 0, W, H);
                else x.drawImage(f, 0, 0, W, H);
                await consume(onFrame, ring[k], i, t);
                progress.emitted = i + 1;
            }
        } finally {
            progress.rescues = stream.rescues || 0;
            progress.silences = stream.silences || 0;
            progress.reordered = stream.reordered || 0;
            try { await cursor.close(); } catch (_) { }
        }
        // what the stream had to mend on THIS device — B-frames out of order
        // (Safari ≤ 26), a decoder replaced — said once, only when it happened
        if (progress.reordered || progress.rescues) {
            feature('vbr_frames_healed', { reordered: progress.reordered, rescues: progress.rescues, silences: progress.silences, codec: progress.codec, frames: N });
        }
        return { frames: N, method: 'decoder' };
    }

    /* ───────────────── lane 2 — <video> seeking ───────────────── */

    function seeked(video, timeoutMs) {
        return new Promise(function (resolve) {
            var done = false;
            function finish() { if (!done) { done = true; resolve(); } }
            video.onseeked = finish;
            setTimeout(finish, timeoutMs || 6000);
        });
    }
    /* The frame a seek lands on, not the one before it: `seeked` fires before
       the picture is presented, and a draw at that moment captures the previous
       frame in a measured 4 % of cases (timbrica-core.js __seekPresented). */
    function seekTo(video, t) {
        if (typeof root.__seekPresented === 'function') return root.__seekPresented(video, t, 6000);
        video.currentTime = t;
        return seeked(video, 6000);
    }

    async function iterateVideoSeek(opts, onFrame, startAt) {
        var video = document.createElement('video');
        /* Derive the URL when the caller only handed us the File.
         *
         *  This path used to read `opts.url` and nothing else. /video-bg-remover
         *  passes `file` AND `url`; /text-behind-image passes only `file`, so
         *  `video.src` became the string "undefined", the browser fetched it,
         *  got a 404 and fired onerror → `Error('decode')`. A FALLBACK that
         *  needs an argument the primary path does not is the worst kind of
         *  trap: everything looks fine until the primary actually bails, i.e. on
         *  exactly the devices that depend on the fallback. Measured 25.08:
         *  text-behind-image's video export died here every single time, which
         *  is the `export | decode` pair sitting in prod tool_errors. */
        var ownUrl = '';
        if (!opts.url && opts.file) { ownUrl = URL.createObjectURL(opts.file); }
        video.muted = true;
        video.preload = 'auto';
        video.playsInline = true;
        attachHidden(video);
        video.src = opts.url || ownUrl;
        try { video.load(); } catch (_) { }
        try {
            await whenLoaded(video, 20000);
        } catch (e) {
            detachHidden(video);
            try { video.removeAttribute('src'); video.load(); } catch (_) { }
            if (ownUrl) URL.revokeObjectURL(ownUrl);
            throw e;
        }

        var plan = planOf(opts);
        var N = plan.n;
        var W = opts.width, H = opts.height;
        var ring = [makeCanvas(W, H), makeCanvas(W, H)];
        var ctxs = [ring[0].getContext('2d'), ring[1].getContext('2d')];

        try {
            for (var i = (startAt | 0); i < N; i++) {
                if (aborted(opts.abortSignal)) throw abortError();
                var t = plan.times ? plan.at(i) : Math.min(opts.trimStart + i / opts.fps, opts.trimEnd - 0.001);
                // a frame of its own time is sought in its MIDDLE: a seek onto its
                // first instant may land on the picture before it
                await seekTo(video, Math.max(0, plan.times ? Math.min(plan.mid(i), plan.trimEnd - 0.001) : t));
                var k = i & 1;
                ctxs[k].clearRect(0, 0, W, H);
                ctxs[k].drawImage(video, 0, 0, W, H);
                await consume(onFrame, ring[k], i, t);
            }
        } finally {
            try { video.removeAttribute('src'); video.load(); } catch (_) { }
            detachHidden(video);
            if (ownUrl) URL.revokeObjectURL(ownUrl);   // also on abort — a cancelled
        }                                              // export must not leak the clip
        return { frames: N, method: (startAt | 0) > 0 ? 'video-seek-resume' : 'video-seek' };
    }

    /* ───────────────── dispatch ───────────────── */

    async function iterate(opts, onFrame) {
        var progress = { emitted: 0, rescues: 0, silences: 0, codec: '', container: '' };
        try {
            return await iterateDecoder(opts, onFrame, progress);
        } catch (e) {
            if (e && e.name === 'AbortError') throw e;
            if (e && e.fromConsumer) throw e;
            var msg = String((e && e.message) || e || '');
            /* The seek floor RESUMES at the first frame lane 1 did not deliver —
               it must never restart at 0. Every consumer has already acted on the
               frames it got: remove-text's exporter handed them to the hardware
               encoder (a restart re-emitted frames the encoder had consumed and
               froze its bridge for good, report 2026-07-29), and RVM is
               recurrent — re-running frames 0..k smears the matte. */
            feature('vbr_decode_fallback', {
                reason: msg.slice(0, 80), resume_at: progress.emitted, lane: 'decoder',
                rescues: progress.rescues, silences: progress.silences,
                codec: progress.codec, container: progress.container
            });
            /* A caller that owns a better floor says so: /remove-object-from-video
               seeks the display video it already has on screen, which iOS is
               guaranteed to decode. It gets the failure and the resume index
               instead of a second element. */
            if (opts.noSeekFallback) {
                var fe = new Error(msg || 'decoder-lane-failed');
                fe.resumeAt = progress.emitted;
                fe.webcodecsFailed = true;
                throw fe;
            }
            return await iterateVideoSeek(opts, onFrame, progress.emitted);
        }
    }

    /* ───────────────── the clip's own frame times ─────────────────
       The presentation time of every picture, from the sample table the decoder
       lane reads (mp4/mov, WebM/MKV) — the moov or the cues, not the pictures.
       null when the table cannot be read cheaply (another container, a
       fragmented file over EAGER_TIMES) or makes no sense against the element's
       duration: the caller keeps its grid. Frames closer than MIN_GAP_S to the
       previous one (two pictures stamped the same instant) are one moment — the
       cursor shows the first of them, as a player does. */
    var EAGER_TIMES = 64 * 1048576, MIN_GAP_S = 0.0005;
    async function frameTimes(file, o) {
        o = o || {};
        if (!file) return null;
        try {
            var VM = await decoderModule();
            if (!VM.presentationTimesUs || (!VM.isMp4Family(file) && !VM.isMatroska(file))) return null;
            await VM.loadMp4Box();
            var d = await VM.demuxClip(file, false, { matroska: true, slug: o.slug, eagerMax: Math.min(EAGER_TIMES, eagerCeiling()) });
            if (!d || !d.samples || d.samples.length < 2) return null;
            var us = VM.presentationTimesUs(d), dur = o.duration > 0 ? o.duration : Infinity, times = [];
            for (var i = 0; i < us.length; i++) {
                var t = us[i] / 1e6;
                if (t < 0 || t >= dur) continue;                      // before the start / after the end of what is shown
                if (times.length && t - times[times.length - 1] < MIN_GAP_S) continue;
                times.push(t);
            }
            if (times.length < 2 || times[0] > 1) return null;
            var span = isFinite(dur) ? dur : times[times.length - 1];
            var avg = times.length / Math.max(1e-3, span);
            if (!(avg >= 1 && avg <= 240)) return null;
            // «variable»: more than 2 % of the gaps a quarter off the median one
            var gaps = [];
            for (var g = 1; g < times.length; g++) gaps.push(times[g] - times[g - 1]);
            var sg = gaps.slice().sort(function (a, b) { return a - b; }), med = sg[sg.length >> 1], off = 0;
            for (var h = 0; h < gaps.length; h++) if (Math.abs(gaps[h] - med) > 0.25 * med) off++;
            return { times: times, avgFps: avg, medianFps: med > 0 ? 1 / med : avg, vfr: off > 0.02 * gaps.length };
        } catch (e) {
            return null;
        }
    }
    function lastAtOrBefore(tt, t) {
        var lo = 0, hi = tt.length;
        while (lo < hi) { var m = (lo + hi) >> 1; if (tt[m] <= t + 1e-6) lo = m + 1; else hi = m; }
        return Math.max(0, lo - 1);
    }
    /* Frame index ↔ time over the clip's own frames, for engines that keep a
       frame ring: at(k) — when frame k is shown; onScreen(t) — the frame shown
       at t; firstFrom(t) — the first frame shown at or after t. */
    function timeline(tt) {
        var n = tt.length;
        return {
            n: n, times: tt,
            at: function (k) { return tt[Math.max(0, Math.min(n - 1, k))]; },
            onScreen: function (t) { return lastAtOrBefore(tt, t); },
            firstFrom: function (t) {
                var k = lastAtOrBefore(tt, t);
                return (tt[k] < t - 1e-6) ? Math.min(n, k + 1) : k;
            }
        };
    }

    root.VbrFrameSource = { iterate: iterate, frameTimes: frameTimes, timeline: timeline, attachHidden: attachHidden, detachHidden: detachHidden, whenLoaded: whenLoaded };
})(typeof window !== 'undefined' ? window : globalThis);
